/**
 * Unilar BF Vitrine
 * Drag carousel + progress bar (mobile + desktop).
 */

class UnilarBfVitrine {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-bf-vitrine-track]');
    this.progressWrap = section.querySelector('[data-bf-vitrine-progress]');
    this.slides = [];
    this.segments = [];
    this.scrollRaf = 0;
    this.drag = {
      active: false,
      moved: false,
      startX: 0,
      scrollLeft: 0,
      pointerId: null,
    };
    this.init();
  }

  init() {
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('.unilar-bf-vitrine__slide'));
    this.initProgress();
    this.initDrag();

    window.addEventListener('resize', () => {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => this.updateProgressVisibility());
    });
  }

  initProgress() {
    if (!this.progressWrap || this.slides.length < 2) return;

    this.progressWrap.replaceChildren();
    this.segments = [];

    this.slides.forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'unilar-bf-vitrine__progress-seg';
      button.setAttribute('aria-label', `Ir para produto ${index + 1}`);
      button.addEventListener('click', () => this.scrollToSlide(index));
      this.progressWrap.appendChild(button);
      this.segments.push(button);
    });

    this.setActiveSegment(0);
    this.updateProgressVisibility();

    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => this.syncActiveSegment());
      },
      { passive: true }
    );
  }

  updateProgressVisibility() {
    if (!this.progressWrap || !this.track) return;
    const canScroll = this.track.scrollWidth > this.track.clientWidth + 2;
    this.progressWrap.hidden = !canScroll || this.slides.length < 2;
  }

  getSlideScrollLeft(index) {
    const slide = this.slides[index];
    const first = this.slides[0];
    if (!slide || !first) return 0;
    return slide.offsetLeft - first.offsetLeft;
  }

  scrollToSlide(index) {
    if (!this.track) return;
    this.track.scrollTo({
      left: this.getSlideScrollLeft(index),
      behavior: 'smooth',
    });
    this.setActiveSegment(index);
  }

  syncActiveSegment() {
    if (!this.track || !this.slides.length) return;

    const paddingLeft = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    const origin = this.track.getBoundingClientRect().left + paddingLeft;
    let activeIndex = 0;
    let closest = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - origin);
      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });

    this.setActiveSegment(activeIndex);
  }

  setActiveSegment(index) {
    this.segments.forEach((segment, segmentIndex) => {
      segment.classList.toggle('is-active', segmentIndex === index);
      if (segmentIndex === index) {
        segment.setAttribute('aria-current', 'true');
      } else {
        segment.removeAttribute('aria-current');
      }
    });
  }

  initDrag() {
    if (!this.track) return;

    const interactiveSelector = 'a, button, input, label, textarea, select';

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.target instanceof Element && event.target.closest(interactiveSelector)) return;

      this.drag.active = true;
      this.drag.moved = false;
      this.drag.startX = event.clientX;
      this.drag.scrollLeft = this.track.scrollLeft;
      this.drag.pointerId = event.pointerId;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!this.drag.active) return;
      const delta = event.clientX - this.drag.startX;
      if (Math.abs(delta) > 4) this.drag.moved = true;
      this.track.scrollLeft = this.drag.scrollLeft - delta;
    });

    const stopDrag = () => {
      if (!this.drag.active) return;
      if (this.drag.pointerId != null) {
        try {
          this.track.releasePointerCapture(this.drag.pointerId);
        } catch (error) {
          // Capture may already be released.
        }
      }
      this.drag.active = false;
      this.drag.pointerId = null;
      this.track.classList.remove('is-dragging');
    };

    this.track.addEventListener('pointerup', stopDrag);
    this.track.addEventListener('pointercancel', stopDrag);
    this.track.addEventListener(
      'click',
      (event) => {
        if (!this.drag.moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.drag.moved = false;
      },
      true
    );
  }
}

function initUnilarBfVitrine() {
  document.querySelectorAll('[data-unilar-bf-vitrine]').forEach((section) => {
    if (!(section instanceof HTMLElement) || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    new UnilarBfVitrine(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnilarBfVitrine);
} else {
  initUnilarBfVitrine();
}

document.addEventListener('shopify:section:load', initUnilarBfVitrine);
